import React, { useState, useEffect, useCallback } from 'react'
import Wrapper from '../../components/Wrapper'
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Popper,
  TextField,
  InputAdornment,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import InfoIcon from '@mui/icons-material/Info'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { useNavigate } from 'react-router-dom'
import { shortFunction, input } from '../../translate'
import Api from '../../Api'
import { greenTheme } from '../../helpers/style'

const { REACT_APP_SERVER } = process.env
const color = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : greenTheme.primary
const hoverColor = REACT_APP_SERVER === 'ATS' ? '#095a8f' : greenTheme.hover
import { toast } from 'react-toastify'
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'

const ZONE_NOT_SELECTED = 'zone-not-selected'
const ZONE_SELECTED = 'zone-selected'

function SipCard({ sipItem }) {
  const id = sipItem.buddy || String(sipItem.id)
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: { sipItem },
  })
  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 0.75,
        px: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: isDragging ? 'grabbing' : 'grab',
        bgcolor: isDragging ? 'action.hover' : 'transparent',
        opacity: isDragging ? 0.35 : 1,
        transition: 'opacity 0.2s',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <DragIndicatorIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: 18 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {sipItem.buddy || sipItem.id} — {sipItem.notes || sipItem.email || ''}
        </Typography>
        {sipItem.provider ? (
          <Typography variant="caption" color="text.secondary">
            {sipItem.provider}
          </Typography>
        ) : null}
      </Box>
    </Box>
  )
}

function matchSipItem(item, q) {
  if (!q || !String(q).trim()) return true
  const s = String(q).toLowerCase().trim()
  const fields = [
    item.buddy,
    item.id,
    item.notes,
    item.email,
    item.provider,
  ].filter(Boolean).map((x) => String(x).toLowerCase())
  return fields.some((f) => f.includes(s))
}

function DropBlock({ id, title, count, children, isEmpty, searchValue, onSearchChange, searchPlaceholder, primaryColor }) {
  const { setNodeRef, isOver } = useDroppable({ id })
  const c = primaryColor || greenTheme.primary
  return (
    <Box
      ref={setNodeRef}
      sx={{
        flex: 1,
        minWidth: 0,
        border: '2px solid',
        borderColor: isOver ? c : 'divider',
        borderRadius: 2,
        bgcolor: isOver ? `${c}10` : 'grey.50',
        transition: 'border-color 0.2s, background-color 0.2s',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          py: 1,
          px: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} color="text.primary">
          {title} ({count})
        </Typography>
        {onSearchChange != null && (
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={searchValue ?? ''}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: c },
                '&:hover fieldset': { borderColor: c },
                '&.Mui-focused fieldset': { borderColor: c },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: c },
            }}
            fullWidth
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: c }} />
                </InputAdornment>
              ),
              endAdornment: (searchValue ?? '') ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => onSearchChange('')} edge="end" aria-label="clear">
                    <ClearIcon fontSize="small" sx={{ color: c }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 120,
          maxHeight: '40vh',
          overflowY: 'auto',
          p: 0.5,
        }}
      >
        {isEmpty ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
            —
          </Typography>
        ) : (
          children
        )}
      </Box>
    </Box>
  )
}

function SipSpeech() {
  const langIndex = +localStorage.getItem('atsLang') || 1
  const navigate = useNavigate()
  const title = shortFunction.speech?.[langIndex] || 'Speech to text'

  const [sipList, setSipList] = useState([])
  const [selectedSpeech, setSelectedSpeech] = useState([])
  const [loadingSips, setLoadingSips] = useState(false)
  const [saving, setSaving] = useState(false)
  const [activeId, setActiveId] = useState(null)
  const [searchNotSelected, setSearchNotSelected] = useState('')
  const [searchSelected, setSearchSelected] = useState('')
  const activeItem = activeId && sipList.find((item) => (item.buddy || String(item.id)) === activeId)
  const [helpAnchor, setHelpAnchor] = useState(null)
  const helpCloseTimeoutRef = React.useRef(null)
  const handleHelpOpen = (e) => {
    if (helpCloseTimeoutRef.current) clearTimeout(helpCloseTimeoutRef.current)
    setHelpAnchor(e.currentTarget)
  }
  const handleHelpClose = () => {
    helpCloseTimeoutRef.current = setTimeout(() => setHelpAnchor(null), 150)
  }
  const handleHelpCloseClear = () => {
    if (helpCloseTimeoutRef.current) clearTimeout(helpCloseTimeoutRef.current)
  }

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  )

  const fetchSips = useCallback(async () => {
    try {
      setLoadingSips(true)
      const { data } = await Api.shortSipList()
      const list = Array.isArray(data?.data) ? data.data : []
      setSipList(list)
      setSelectedSpeech(list.filter((item) => +item.isspeech === 1).map((item) => item.buddy || item.id))
    } catch (e) {
      console.log(e)
      setSipList([])
      setSelectedSpeech([])
    } finally {
      setLoadingSips(false)
    }
  }, [])

  useEffect(() => {
    fetchSips()
  }, [fetchSips])

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id)
  }, [])

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event
    setActiveId(null)
    if (!over) return
    const buddy = active.id
    if (over.id === ZONE_SELECTED) {
      setSelectedSpeech((prev) => (prev.includes(buddy) ? prev : [...prev, buddy]))
    } else if (over.id === ZONE_NOT_SELECTED) {
      setSelectedSpeech((prev) => prev.filter((b) => b !== buddy))
    }
  }, [])

  const handleSave = useCallback(async () => {
    try {
      setSaving(true)
      await Api.shortSipSpeech(selectedSpeech)
      toast.success(shortFunction.operator_save?.[langIndex] || 'Saved')
    } catch (e) {
      console.log(e)
      toast.error('Failed to save')
    } finally {
      setSaving(false)
    }
  }, [selectedSpeech, langIndex])

  const handleCancel = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const notSelectedItems = sipList.filter(
    (item) => !selectedSpeech.includes(item.buddy || item.id)
  )
  const selectedItems = sipList.filter((item) =>
    selectedSpeech.includes(item.buddy || item.id)
  )
  const filteredNotSelected = notSelectedItems.filter((item) => matchSipItem(item, searchNotSelected))
  const filteredSelected = selectedItems.filter((item) => matchSipItem(item, searchSelected))

  return (
    <Wrapper>
      <div className="tableParent">
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '2px solid rgba(0, 0, 0, 0.08)', pb: 2.5, }}>
            <IconButton
              size="small"
              sx={{ color, fontSize: 18, '&:hover': { backgroundColor: `${color}15` } }}
              onClick={() => navigate(-1)}
              aria-label="Back"
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
              {title}
            </Typography>
            <IconButton
              size="small"
              onMouseEnter={handleHelpOpen}
              onMouseLeave={handleHelpClose}
              aria-label={shortFunction.speech_help_tooltip?.[langIndex] || 'How it works'}
              sx={{ color, '&:hover': { backgroundColor: `${color}15` } }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Box>

          <Popper
            open={Boolean(helpAnchor)}
            anchorEl={helpAnchor}
            placement="bottom-start"
            modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
          >
            <Paper
              elevation={8}
              onMouseEnter={handleHelpCloseClear}
              onMouseLeave={handleHelpClose}
              sx={{ mt: 0, overflow: 'visible', width: 400 }}
            >
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                {shortFunction.speech_help_title?.[langIndex] || 'How to use'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {shortFunction.speech_help_desc?.[langIndex] ||
                  'Drag employees from the left column to the right to enable speech-to-text for their calls.'}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'stretch',
                  minHeight: 72,
                  position: 'relative',
                  '& .help-drag-zone': {
                    flex: 1,
                    borderRadius: 1,
                    border: '2px dashed',
                    borderColor: 'divider',
                    bgcolor: 'grey.100',
                  },
                  '& .help-drag-card': {
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    boxShadow: 2,
                    border: '1px solid',
                    borderColor: color,
                    animation: 'helpDragDemo 3.2s ease-in-out infinite',
                    whiteSpace: 'nowrap',
                  },
                  '@keyframes helpDragDemo': {
                    '0%, 100%': { left: '12px', transform: 'translateY(-50%)', boxShadow: 2 },
                    '12%': { left: '12px', transform: 'translateY(-50%) scale(0.96)', boxShadow: 3 },
                    '18%': { left: '12px', transform: 'translate(-50%, -54%) scale(1)', boxShadow: 5 },
                    '48%': { left: '50%', transform: 'translate(-50%, -54%)', boxShadow: 5 },
                    '56%': { left: 'calc(100% - 12px)', transform: 'translate(-100%, -54%)', boxShadow: 5 },
                    '62%': { left: 'calc(100% - 12px)', transform: 'translate(-100%, -46%)', boxShadow: 2 },
                    '70%': { left: 'calc(100% - 12px)', transform: 'translate(-100%, -50%)', boxShadow: 2 },
                    '82%': { left: 'calc(100% - 12px)', transform: 'translate(-100%, -50%)', boxShadow: 2 },
                  },
                }}
              >
                <Box className="help-drag-zone" />
                <Box className="help-drag-zone" />
                <Box className="help-drag-card">
                  <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" fontWeight={600}>
                    {shortFunction.speech_help_drag_me?.[langIndex] || 'SIP — drag me'}
                  </Typography>
                </Box>
              </Box>
            </Box>
            </Paper>
          </Popper>

          <Alert
            icon={<InfoIcon />}
            severity="success"
            sx={
              REACT_APP_SERVER !== 'ATS'
                ? { mb: 2 }
                : {
                  mb: 2,
                  border: `1px solid ${color}`,
                  backgroundColor: `${color}10`,
                  '& .MuiAlert-icon': { color },
                }
            }
          >
            <Typography variant="body2">
              {shortFunction.speech_desc?.[langIndex] ||
                'Allows to generate text from the call of the selected employee'}
            </Typography>
          </Alert>


          {loadingSips ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} sx={{ color }} />
            </Box>
          ) : !sipList.length ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
              {shortFunction.operator_empty?.[langIndex]}
            </Typography>
          ) : (
            <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <DropBlock
                  id={ZONE_NOT_SELECTED}
                  primaryColor={color}
                  title={shortFunction.speech_not_selected?.[langIndex] || 'Not using speech to text'}
                  count={notSelectedItems.length}
                  isEmpty={!filteredNotSelected.length}
                  searchValue={searchNotSelected}
                  onSearchChange={setSearchNotSelected}
                  searchPlaceholder={input.search[langIndex]}
                >
                  {filteredNotSelected.map((sipItem) => (
                    <SipCard key={sipItem.buddy || sipItem.id} sipItem={sipItem} />
                  ))}
                </DropBlock>
                <DropBlock
                  id={ZONE_SELECTED}
                  primaryColor={color}
                  title={shortFunction.speech_selected?.[langIndex] || 'Using speech to text'}
                  count={selectedItems.length}
                  isEmpty={!filteredSelected.length}
                  searchValue={searchSelected}
                  onSearchChange={setSearchSelected}
                  searchPlaceholder={input.search[langIndex]}
                >
                  {filteredSelected.map((sipItem) => (
                    <SipCard key={sipItem.buddy || sipItem.id} sipItem={sipItem} />
                  ))}
                </DropBlock>
              </Box>

              <DragOverlay
                dropAnimation={{
                  duration: 200,
                  easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
                }}
              >
                {activeItem ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      py: 0.75,
                      px: 1.5,
                      borderRadius: 1,
                      boxShadow: 3,
                      bgcolor: 'background.paper',
                      border: '2px solid',
                      borderColor: color,
                      cursor: 'grabbing',
                      minWidth: 200,
                    }}
                  >
                    <DragIndicatorIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: 18 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {activeItem.buddy || activeItem.id} — {activeItem.notes || activeItem.email || ''}
                      </Typography>
                      {activeItem.provider ? (
                        <Typography variant="caption" color="text.secondary">
                          {activeItem.provider}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                color,
                borderColor: color,
                textTransform: 'none',
                '&:hover': { borderColor: color, backgroundColor: `${color}15` },
              }}
            >
              {shortFunction.operator_close?.[langIndex]}
            </Button>
            <Button
              variant="contained"
              disabled={!selectedSpeech.length || saving}
              onClick={handleSave}
              sx={{
                backgroundColor: color,
                color: '#fff',
                textTransform: 'none',
                '&:hover': { backgroundColor: hoverColor },
              }}
            >
              {saving ? '...' : shortFunction.operator_save?.[langIndex]}
            </Button>
          </Box>
        </Paper>
      </div>
    </Wrapper>
  )
}

export default SipSpeech
